import { BadgePercent, PackageCheck, ShoppingBag, Truck, Users, Zap } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const CARDS = [
  {
    icon: ShoppingBag,
    tag: "Flagship",
    title: "Click & Collect Delivery",
    desc: "Order from any store in Canberra — we collect it and run it up the mountain to Cooma, Berridale or Jindabyne. You skip the drive entirely.",
    span: "md:col-span-2 md:row-span-2",
    big: true,
  },
  {
    icon: Zap,
    tag: "Fast",
    title: "Same-Day Dash",
    desc: "Booked early, delivered today. Direct drive, no depot detours.",
    span: "md:col-span-2",
  },
  {
    icon: Truck,
    tag: "Reliable",
    title: "Next-Day Regional Run",
    desc: "Guaranteed next-day drops across the Snowy Mountains route.",
    span: "",
  },
  {
    icon: PackageCheck,
    tag: "Every job",
    title: "Free Tracking & POD",
    desc: "Live tracking in the app, proof of delivery on completion.",
    span: "",
  },
];

export default function ServicesBento() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <Eyebrow>What we carry</Eyebrow>
            <h2 className="font-display mt-4 text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F6F1E3] sm:text-6xl">
              Services built for<br />
              <span className="text-outline-gold">the mountain run</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-[#B9C4B4] sm:text-base">
              One kilted courier, one ute, one van — and a route we know like the
              back of our hand. Same-day or next-day, depending on pickup
              availability.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="services-bento">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className={c.span}>
              <div
                data-testid={`service-card-${i}`}
                className="bento-card group flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-[#122A1F]/70 p-7 backdrop-blur-sm"
              >
                <div className="flex items-start justify-between">
                  <c.icon className="h-8 w-8 text-[#F0A829]" strokeWidth={1.8} />
                  <span className="font-mono-brand rounded-full border border-[#F0A829]/30 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#F0A829]">
                    {c.tag}
                  </span>
                </div>
                <div className={c.big ? "mt-16" : "mt-10"}>
                  <h3 className={`font-display font-bold uppercase tracking-wide text-[#F6F1E3] ${c.big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
                    {c.title}
                  </h3>
                  <p className={`mt-3 leading-relaxed text-[#B9C4B4] ${c.big ? "max-w-md text-base" : "text-sm"}`}>
                    {c.desc}
                  </p>
                  {c.big && (
                    <div className="mt-6" data-testid="service-items-list">
                      <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#F0A829]">
                        If it fits, it rides
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {["Car parts", "Pet food", "Flowers", "Groceries", "Paint tins", "Power tools", "Ski & snow gear", "Phone & laptop parts", "Documents", "Birthday gifts", "Hardware", "Chemist orders"].map((item) => (
                          <span
                            key={item}
                            className="font-mono-brand rounded-full border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-[#B9C4B4]"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.32} className="md:col-span-2">
            <div
              data-testid="promo-card-discount"
              className="bento-card group flex h-full items-center gap-5 rounded-2xl border border-[#C8102E]/50 bg-[#C8102E]/15 p-7"
            >
              <BadgePercent className="h-10 w-10 shrink-0 text-[#FF5C72]" strokeWidth={1.8} />
              <div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-[#F6F1E3]">
                  $5 off your second order
                </h3>
                <p className="mt-1 text-sm text-[#B9C4B4]">
                  After a successful first delivery, your second order takes five bucks off. Loyalty, Highland style.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.4} className="md:col-span-2">
            <div
              data-testid="promo-card-referral"
              className="bento-card group flex h-full items-center gap-5 rounded-2xl border border-[#F0A829]/50 bg-[#F0A829] p-7"
            >
              <Users className="h-10 w-10 shrink-0 text-[#0C1F17]" strokeWidth={1.8} />
              <div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-[#0C1F17]">
                  Referral bonus available
                </h3>
                <p className="mt-1 text-sm text-[#0C1F17]/75">
                  Send a mate our way and you both win. Ask us about the bonus when you book.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
